import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import openInNew from "./Assets/Images/questionUp.svg";
import { TextField, CircularProgress, Tooltip } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useState } from "react";
import { createCompanyQuestion, deleteCompanyQuestion, answerCompanyQuestion, setCompanyQuestionMonitored, pollCompanyQuestionAnswer } from "./api/underwritingApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { useRolePermissions } from "./context/RolePermissionsContext";
import { RestrictedAction } from "./components/RestrictedAction";

// Persists which answer windows are open, across component remounts / re-fetches.
// Key: `${companyId}::${questionText}` -> true. Only mutated on user open/close.
const questionWindowStore = {};

const AccordionItem = ({ title, className, children, expanded, onChange }) => {
  const CustomIcon = () => {
    return (
        <Box
            sx={{
                width: 30, 
                height: 30, 
                backgroundColor: '#FFFFFF', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                boxShadow: '0px 0px 7px rgba(0, 0, 0, 0.29)'
            }}
        >
            <ExpandMoreIcon style={{color: "#5186EC"}}/>
        </Box>
    );
  };
  return (
    <Accordion
        width="90%"
        height="76px"
        expanded={!!expanded}
        onChange={onChange}
        sx={{
          background: "#FAFAFC",
          boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
          borderRadius: "8px",
          opacity: "1",
          '&:before': {
                display: 'none',
            },
          "&.inrisk": {
            background: "#F25A4826",
            boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
            borderRadius: "8px",
            opacity: "1",
          },
        }}
      >
      <AccordionSummary
        expandIcon={<CustomIcon />}
        className={className}
        sx={{
          "&.inrisk": {
            background: "#F25A4826",
            borderRadius: "8px",
          },
          color:"#1A2A56",
          fontSize:"20px",
          fontWeight:"500"
        }}
      >
        {title}
      </AccordionSummary>
      <AccordionDetails sx={{color:"#777A82",fontSize:"14px"}}>{children}</AccordionDetails>
    </Accordion>
  );
};

function QuestionsAccordion({ questions, setQuestions, companyId, tenantId }) {
  const [newQuestion, setNewQuestion] = useState('');
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  // Which answer windows are open, keyed by question text. Backed by a module-level store
  // so an open window survives ANY re-render OR remount (poll refreshes, tab re-renders,
  // re-fetches). A window only closes when the user closes it.
  const keyFor = (q) => `${companyId}::${q}`;
  const [expandedIds, setExpandedIds] = useState(() => {
    const init = {};
    const prefix = `${companyId}::`;
    Object.keys(questionWindowStore).forEach((k) => {
      if (k.startsWith(prefix) && questionWindowStore[k]) init[k.slice(prefix.length)] = true;
    });
    return init;
  });
  const setOpenState = (q, val) => {
    if (val) questionWindowStore[keyFor(q)] = true;
    else delete questionWindowStore[keyFor(q)];
    setExpandedIds((p) => ({ ...p, [q]: val }));
  };
  const toggleExpanded = (q) => setOpenState(q, !expandedIds[q]);
  const openExpanded = (q) => setOpenState(q, true);
  const { permissions } = useRolePermissions();
  
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const URLTenant = getTenantIdFromURL();
  const currentTenant = useSelector(state => state.userInfo.tenantId);
  const effectiveTenantId = tenantId || (isAdmin ? URLTenant : currentTenant);

  const handleQuestionChange = (event) => {
    setNewQuestion(event.target.value);
  };

  const deleteQuestion = async (question) => {
    try {
      setDeletingQuestionId(question.id);
      await deleteCompanyQuestion(effectiveTenantId, companyId, question.id);
      const updatedQuestions = questions.filter(item => item.id !== question.id);
      setQuestions(updatedQuestions);
      toast.success("Question deleted successfully");
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question");
    } finally {
      setDeletingQuestionId(null);
    }
  };

  const handleAddQuestion = async () => {
    const questionText = newQuestion.trim();
    if (questionText.length === 0 || questionText.length > 100) return;

    let created = null;
    try {
      setIsAddingQuestion(true);
      const result = await createCompanyQuestion(effectiveTenantId, companyId, questionText);
      if (result?.success && result?.data) {
        created = { ...result.data, _thinking: true };
        setQuestions((prev) => [...prev, created]);
        openExpanded(created.question || questionText); // keep the window open while the answer loads
        setNewQuestion('');
      } else {
        toast.error("Failed to add question");
      }
    } catch (error) {
      console.error('Error creating new question:', error);
      toast.error("Failed to add question");
    } finally {
      setIsAddingQuestion(false);
    }

    if (!created) return;

    // Answer it right away (the item shows a "thinking" box until the LLM responds).
    try {
      await answerCompanyQuestion(effectiveTenantId, companyId, created.id, questionText); // starts the job (fast)
      const q = await pollCompanyQuestionAnswer(effectiveTenantId, companyId, created.id, { timeoutMs: 170000 }); // wait for it to finish
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === created.id
            ? { ...item, _thinking: false, answer: q.answer, summary: q.summary, citations: q.citations }
            : item
        )
      );
    } catch (e) {
      console.error('Error answering question:', e);
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === created.id
            ? { ...item, _thinking: false, summary: "The answer is taking a while — re-open the question in a moment." }
            : item
        )
      );
    }
  };

  const handleToggleMonitor = async (row) => {
    const next = !row.monitored;
    setQuestions((prev) => prev.map((q) => (q.id === row.id ? { ...q, monitored: next } : q)));
    try {
      await setCompanyQuestionMonitored(effectiveTenantId, companyId, row.id, next);
      toast.success(next ? "Monitoring on — you'll get a notification if this becomes true." : "Monitoring off.");
    } catch (e) {
      console.error('Error toggling monitor:', e?.response?.status, e?.response?.data || e?.message);
      setQuestions((prev) => prev.map((q) => (q.id === row.id ? { ...q, monitored: !next } : q)));
      toast.error(e?.response?.data?.message || "Failed to update monitoring. Please try again.");
    }
  };

  const handleRefreshAnswer = async (row) => {
    openExpanded(row.question); // keep the window open while re-answering
    setQuestions((prev) => prev.map((q) => (q.id === row.id ? { ...q, _thinking: true } : q)));
    try {
      await answerCompanyQuestion(effectiveTenantId, companyId, row.id, row.question); // starts the job (fast)
      const q = await pollCompanyQuestionAnswer(effectiveTenantId, companyId, row.id, { timeoutMs: 170000 }); // wait for it to finish
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? { ...item, _thinking: false, answer: q.answer, summary: q.summary, citations: q.citations }
            : item
        )
      );
    } catch (e) {
      console.error('Error refreshing answer:', e);
      // Never leave the answer blank: keep whatever text was there and surface a clear message.
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? { ...item, _thinking: false, summary: item.summary || "The answer is taking longer than usual — re-open or refresh the question in a moment." }
            : item
        )
      );
      toast.info("The answer is taking a while — it'll update shortly. You can re-open the question in a moment.");
    }
  };

  const questionCharactersRemaining = 100 - newQuestion.length;

  if (!questions) {
    return <p>No Questions Available</p>;
  }

  const sortedQuestions = [...questions].sort(
    (a, b) => {
      if (a.answer === true && b.answer !== true) return -1;
      if (a.answer !== true && b.answer === true) return 1;
      return (a.template?.position || 0) - (b.template?.position || 0);
    }
  );

  const cardStyle = { 
    height: '175px',
    boxShadow : 'inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029', 
    position:'relative',
    padding:"20px"
  }

  const typoStyle = { 
    color:"#2F3D63" , 
    fontSize: "20px" , 
    alignContent: "left" , 
    fontWeight: '500',
    paddingTop:"10px"
  }

  return (
    <Box>
      {/* Ask a question that is asked ONLY for this company (answered on next underwrite). */}
      {permissions.canCreateEntity && (
        <Card
          variant="outlined"
          style={{
            boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <Typography style={{ color: "#2F3D63", fontSize: "18px", fontWeight: 500 }}>
            Ask a question about this company
          </Typography>
          <Typography style={{ color: "#777A82", fontSize: "13px", marginTop: "2px", marginBottom: "14px" }}>
            Company-specific — asked only for this company and answered on its next underwrite.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
            <TextField
              sx={{ flex: 1, minWidth: 280 }}
              size="small"
              placeholder="e.g. Does the company rely on a single major customer?"
              variant="outlined"
              value={newQuestion}
              onChange={handleQuestionChange}
              disabled={isAddingQuestion}
              inputProps={{ maxLength: 100 }}
              helperText={`${questionCharactersRemaining} characters remaining`}
            />
            <Button
              variant="contained"
              onClick={handleAddQuestion}
              disabled={newQuestion.trim().length === 0 || newQuestion.length > 100 || isAddingQuestion}
              sx={{
                mt: "1px",
                height: "40px",
                minWidth: "96px",
                backgroundColor: "#5186EC",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { backgroundColor: "#3f6fd1" },
                "&:disabled": { backgroundColor: "#c7cedd", color: "#fff" },
              }}
            >
              {isAddingQuestion ? "Adding…" : "Add question"}
            </Button>
          </Box>
        </Card>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, background: "#F6F8FC", border: "1px solid #e3e8f2", borderRadius: "10px", padding: "12px 14px", marginBottom: "20px" }}>
        <NotificationsActiveIcon sx={{ color: "#5186EC", fontSize: 20, marginTop: "1px", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "13px", color: "#4a5570", lineHeight: 1.55 }}>
          <b>Monitoring:</b> click the <NotificationsNoneIcon sx={{ fontSize: 15, verticalAlign: "-3px" }} /> bell on any
          question to monitor it. Each time CrediArc re-answers that question — on the next underwrite or when you
          re-run it — and the answer is <b>true</b>, a notification is added to the bell menu at the top of the page.
          You're alerted once per change to true (and re-armed if it later goes back to false).
        </Typography>
      </Box>

      <List sx={{display:"flex",flexDirection:'column',gap:"20px"}}>
        {sortedQuestions.map((row) => {
          return (
            <AccordionItem
              title={
                row._thinking ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <span>{row.question}</span>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "#5186EC", fontSize: "13px", fontWeight: 400 }}>
                      <CircularProgress size={13} thickness={5} sx={{ color: "#5186EC" }} />
                      <span style={{ fontStyle: "italic" }}>Analyzing…</span>
                    </Box>
                  </Box>
                ) : (
                  row.question
                )
              }
              expanded={!!expandedIds[row.question] || row._thinking}
              onChange={() => toggleExpanded(row.question)}
              key={row.id}
              className={row.template?.inRisk ? "inrisk" : ""}
            >
              <div className="flex flex-row justify-between align-top gap-2">
                {row._thinking ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", background: "#EEF3FF", border: "1px solid #cfe0ff", borderRadius: "8px", px: 1.5, py: 1.25, width: "100%" }}>
                    <CircularProgress size={16} thickness={5} sx={{ color: "#5186EC" }} />
                    <Typography variant="body2" sx={{ color: "#2f57c4", fontStyle: "italic" }}>
                      Finding the answer for this company…
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ flex: 1, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#2F3D63", lineHeight: 1.6 }}>
                    {row.summary}
                  </Typography>
                )}

                <div style={{ display: "flex", alignItems: "flex-start", gap: "2px", flexShrink: 0 }}>
                <Tooltip arrow title="Re-run the AI answer for this question">
                  <span>
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleRefreshAnswer(row); }}
                      disabled={row._thinking}
                      style={{ minWidth: "40px", color: "#5186EC", opacity: row._thinking ? 0.5 : 1 }}
                    >
                      <RefreshIcon
                        fontSize="small"
                        style={{ animation: row._thinking ? "spin 1s linear infinite" : "none" }}
                      />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  arrow
                  title={row.monitored
                    ? "Monitored — a notification is added whenever the answer is true. Click to stop."
                    : "Monitor this question — get a notification if the answer becomes true"}
                >
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleToggleMonitor(row); }}
                    style={{ minWidth: "40px", color: row.monitored ? "#5186EC" : "#9aa3b8" }}
                  >
                    {row.monitored ? <NotificationsActiveIcon fontSize="small" /> : <NotificationsNoneIcon fontSize="small" />}
                  </Button>
                </Tooltip>
                <RestrictedAction allowed={permissions.canCreateEntity}>
                <Button
                  onClick={(e) => { e.stopPropagation(); deleteQuestion(row); }}
                  disabled={deletingQuestionId === row.id}
                  style={{
                    color: deletingQuestionId === row.id ? "#ccc" : "#5186EC",
                    opacity: deletingQuestionId === row.id ? 0.5 : 1,
                  }}
                >
                  <img
                    src="/binIcon.png"
                    alt="Bin Icon"
                    style={{
                      width: "30px",
                      height: "22px",
                      marginTop: "5px",
                      opacity: deletingQuestionId === row.id ? 0.5 : 1,
                    }}
                  />
                </Button>
                </RestrictedAction>
                </div>
              </div>

              <List sx={{ padding: 0 }}>
                {row.citations?.map((citation) => (
                  <ListItem key={citation.id} href={citation.link} target="_blank" rel="noopener noreferrer" sx={{padding:0,display:"flex",width:"fit-content"}}>
                    <Button
                      size="small"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none",padding:"0 10px 0 5px",minWidth:"fit-content" }}
                      href={citation.link}
                    >
                      <img src={openInNew} alt="Open in new tab"/>
                    </Button>
                    <ListItemText
                      primary={
                        <>
                          {citation.text} - <span style={{ fontStyle: 'italic' }}>{citation.date}</span>
                        </>
                      }
                      sx={{ textAlign: "right" }}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionItem>
            
          );
        })}
      </List>
    </Box>
  );
}

export default QuestionsAccordion;